/**
 * Command palette overlay.
 *
 * A ⌘⇧P fuzzy launcher over the command registry. Lazy-mounted by `KeyboardHost`
 * only while open, so command churn never costs anything when closed. Arrow keys
 * move the selection, Enter runs it (closing the palette first), and each row
 * shows its keyboard shortcut when the command declares a `bindingId`.
 */

import { FC, useMemo, useState } from 'react';
import { Modal, Input, List, Empty, Typography, theme } from 'antd';
import { useSnapshot } from 'valtio';
import FlexBox from '@/components/atoms/FlexBox';
import { commandRegistryState, commandRegistryActions } from './commandRegistryStore';
import { keyboardOverlayActions, keyboardOverlayState } from './keyboardOverlayStore';
import { keybindingRegistry } from './keybindingRegistry';
import { formatChord } from './formatChord';
import { getPlatform } from './platform';

const { Text } = Typography;

export const CommandPalette: FC = () => {
  const overlay = useSnapshot(keyboardOverlayState);
  const { commands } = useSnapshot(commandRegistryState);
  const { token } = theme.useToken();
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const platform = getPlatform();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = commands.filter((c) => c.enabled !== false);
    if (!q) return list;
    return list.filter((c) => `${c.label} ${c.group} ${c.keywords ?? ''}`.toLowerCase().includes(q));
  }, [commands, query]);

  const runAt = (idx: number): void => {
    const cmd = filtered[idx];
    if (!cmd) return;
    keyboardOverlayActions.closePalette();
    commandRegistryActions.run(cmd.id);
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runAt(activeIdx);
    }
  };

  const shortcutFor = (bindingId?: string): string | null => {
    if (!bindingId) return null;
    const binding = keybindingRegistry.byId(bindingId);
    const chord = binding?.chords[0];
    return chord ? formatChord(chord, platform) : null;
  };

  return (
    <Modal
      open={overlay.paletteOpen}
      onCancel={keyboardOverlayActions.closePalette}
      footer={null}
      closable={false}
      title={null}
      width={560}
      styles={{ body: { paddingTop: 5 } }}
    >
      <Input
        autoFocus
        placeholder="Type a command…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActiveIdx(0);
        }}
        onKeyDown={onKeyDown}
        allowClear
      />
      {filtered.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No commands" />
      ) : (
        <List
          size="small"
          dataSource={filtered}
          style={{ marginTop: 5, maxHeight: 360, overflowY: 'auto' }}
          renderItem={(cmd, idx) => {
            const shortcut = shortcutFor(cmd.bindingId);
            return (
              <List.Item
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => runAt(idx)}
                style={{
                  cursor: 'pointer',
                  paddingInline: 10,
                  borderRadius: token.borderRadius,
                  background: idx === activeIdx ? token.controlItemBgActive : undefined,
                }}
              >
                <FlexBox gap={10} style={{ width: '100%' }}>
                  <Text>{cmd.label}</Text>
                  <FlexBox gap={10} noGrow justifyEnd>
                    <Text type="secondary">{cmd.group}</Text>
                    {shortcut ? <Text keyboard>{shortcut}</Text> : null}
                  </FlexBox>
                </FlexBox>
              </List.Item>
            );
          }}
        />
      )}
    </Modal>
  );
};
